import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { PlatformIntegration } from '../types';

const PlatformShowcase: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>('All');

  const platforms: PlatformIntegration[] = [
  {
    id: '1',
    name: 'Salesforce',
    logo: "https://images.unsplash.com/photo-1728410539013-ad662a093c68",
    alt: 'Salesforce CRM platform logo with blue cloud icon',
    category: 'CRM',
    description: 'Complete CRM integration with custom objects, workflows, and real-time data sync.',
    features: ['Custom Objects', 'Workflow Automation', 'Real-time Sync', 'Apex Integration']
  },
  {
    id: '2',
    name: 'SAP',
    logo: "https://images.unsplash.com/photo-1565174967408-1ada6cf19bd5",
    alt: 'SAP enterprise software logo with blue and white design',
    category: 'ERP',
    description: 'Enterprise resource planning integration with financial and operational modules.',
    features: ['Financial Modules', 'Inventory Management', 'HR Integration', 'Custom Reports']
  },
  {
    id: '3',
    name: 'Shopify',
    logo: "https://images.unsplash.com/photo-1578401079419-1ab3f3b339ae",
    alt: 'Shopify e-commerce platform logo with green shopping bag icon',
    category: 'E-commerce',
    description: 'E-commerce platform integration with inventory, orders, and customer management.',
    features: ['Product Sync', 'Order Management', 'Customer Data', 'Webhook Events']
  },
  {
    id: '4',
    name: 'Stripe',
    logo: "https://images.unsplash.com/photo-1680128370051-5abace0dfcd9",
    alt: 'Stripe payment processing logo with purple and blue gradient',
    category: 'Payment',
    description: 'Payment processing integration with subscription management and invoicing.',
    features: ['Payment Processing', 'Subscriptions', 'Invoicing', 'Webhook Handling']
  },
  {
    id: '5',
    name: 'HubSpot',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_19eefe0f7-1763713776090.png",
    alt: 'HubSpot marketing automation logo with orange sprocket icon',
    category: 'Marketing',
    description: 'Marketing automation integration with lead tracking and campaign management.',
    features: ['Lead Tracking', 'Email Campaigns', 'Analytics', 'Contact Management']
  },
  {
    id: '6',
    name: 'QuickBooks',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1daa4c628-1763713775164.png",
    alt: 'QuickBooks accounting software logo with green circular design',
    category: 'Accounting',
    description: 'Accounting software integration with invoicing, expenses, and financial reporting.',
    features: ['Invoice Sync', 'Expense Tracking', 'Financial Reports', 'Tax Management']
  }];


  const categories = ['All', 'CRM', 'ERP', 'E-commerce', 'Payment', 'Marketing', 'Accounting'];

  const filteredPlatforms = activeCategory === 'All' ?
  platforms :
  platforms.filter((p) => p.category === activeCategory);

  return (
    <section className="py-16 lg:py-24 bg-surface">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-4">
            Platform Integration Expertise
          </h2>
          <p className="text-lg text-text-secondary">
            Seamlessly connect with 50+ enterprise platforms and custom systems
          </p>
        </div>
        
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) =>
          <button
            key={category}
            onClick={() => setActiveCategory(category)}
            className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-fast ${
            activeCategory === category ?
            'bg-primary text-white shadow-md' :
            'bg-card text-text-secondary hover:bg-surface border border-border'}`
            }>

              {category}
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPlatforms.map((platform) =>
          <div
            key={platform.id}
            className="bg-card p-6 rounded-lg border border-border hover:shadow-lg transition-all duration-fast group">

              <div className="flex items-center space-x-4 mb-4">
                <div className="w-16 h-16 rounded-lg overflow-hidden border border-border flex-shrink-0">
                  <Image
                  src={platform.logo}
                  alt={platform.alt}
                  className="w-full h-full object-cover" />

                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-text-primary mb-1">
                    {platform.name}
                  </h3>
                  <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                    {platform.category}
                  </span>
                </div>
              </div>
              
              <p className="text-text-secondary mb-4 leading-relaxed">
                {platform.description}
              </p>
              
              <div className="space-y-2">
                {platform.features.map((feature, index) =>
              <div key={index} className="flex items-center space-x-2">
                    <Icon name="Check" size={16} className="text-success flex-shrink-0" />
                    <span className="text-sm text-text-secondary">{feature}</span>
                  </div>
              )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

export default PlatformShowcase;